import React, {
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import {
  Card,
  Spinner,
  Button,
  Modal,
  TextInput,
  Label,
  Alert,
} from "flowbite-react";
import { Store } from "../context/store";
import { useNavigate } from "react-router-dom";
import apiRequest from "../utils/apiRequest";
import { HiOutlineExclamationCircle, HiCheckCircle } from "react-icons/hi";
import { useAllPermissions } from "../utils/usePermission";
import DashboardLayout from "../components/layout/DashboardLayout";

// Currency formatter for clean display
const formatCurrency = (amount) => {
  if (amount === undefined || amount === null) return "N/A";
  return `₦ ${parseFloat(amount).toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;
};

function SiteSettings() {
  let store = useContext(Store);
  let [baseUrl] = store.url;

  const navigate = useNavigate();

  // State consolidation
  const [loading, setLoading] = useState(true);
  const [currentSettings, setCurrentSettings] = useState(null);
  const [newOpex, setNewOpex] = useState("");
  const [notification, setNotification] = useState({ type: null, message: "" });

  // Modal states
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const { canWriteSettings } = useAllPermissions();

  // --- API Handlers ---

  const loadSettings = useCallback(async () => {
    setLoading(true);
    setNotification({ type: null, message: "" });
    try {
      let token = localStorage.getItem("royal-token");
      const url = `${baseUrl}/settings`;
      const { data } = await apiRequest(url, "GET", null, token);

      setCurrentSettings(data);
      setNewOpex(data?.oPExLimit || ""); // Initialize newOpex with current value
    } catch (error) {
      const unauthorized =
        error.message.includes("Access Denied") ||
        error.message.includes("token");
      if (unauthorized) {
        setNotification({
          type: "warning",
          message: "Session Expired! Please log in again.",
        });
        setShowLogoutModal(true);
        localStorage.removeItem("royal-token");
      } else {
        setNotification({
          type: "failure",
          message: error.message || "Failed to load settings.",
        });
      }
    } finally {
      setLoading(false);
    }
  }, [baseUrl]);

  const changeMaxOpex = async () => {
    setShowConfirmModal(false);
    setLoading(true);
    setNotification({ type: null, message: "" });

    // Client-side validation
    const opexValue = parseFloat(newOpex);
    if (isNaN(opexValue) || opexValue <= 0) {
      setNotification({
        type: "failure",
        message: "Please enter a valid amount greater than zero.",
      });
      setLoading(false);
      return;
    }

    try {
      let token = localStorage.getItem("royal-token");
      const url = `${baseUrl}/settings/${currentSettings.id}`;

      const { data } = await apiRequest(
        url,
        "PUT",
        { oPExLimit: opexValue },
        token,
      );

      setCurrentSettings(data); // Update local state with fresh data from server
      setNotification({
        type: "success",
        message: "Maximum OpEx Limit successfully updated.",
      });
    } catch (error) {
      setNotification({
        type: "failure",
        message: error.message || "Failed to update OpEx limit.",
      });
    } finally {
      setLoading(false);
    }
  };

  // --- Effects and Helpers ---

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const logOut = () => {
    navigate("/");
  };

  const isUpdateDisabled = useMemo(() => {
    const currentValue = currentSettings?.oPExLimit;
    const newValue = parseFloat(newOpex);
    return (
      loading || isNaN(newValue) || newValue <= 0 || newValue === currentValue
    );
  }, [loading, newOpex, currentSettings]);

  // --- UI Render ---

  return (
    <>
      <title>Site Settings</title>
      <DashboardLayout active="siteSettings" contentClassName="lg:p-8">
            <h5 className="text-xl font-bold leading-none text-gray-900 dark:text-white">
              ⚙️ Site Configuration Settings
            </h5>

            {loading ? (
              <div className="flex justify-center items-center h-[50vh]">
                <Spinner size="xl" color="info" />
              </div>
            ) : (
              <Card className="rounded-xl my-4 min-h-[50vh] shadow-lg dark:bg-gray-800">
                {/* Notification Area */}
                {notification.type && (
                  <Alert
                    color={notification.type}
                    onDismiss={() =>
                      setNotification({ type: null, message: "" })
                    }
                    icon={
                      notification.type === "success"
                        ? HiCheckCircle
                        : HiOutlineExclamationCircle
                    }
                    className="mb-4"
                  >
                    <span className="font-medium">{notification.message}</span>
                  </Alert>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-start">
                  {/* Current Setting Display */}
                  <div className="p-4 rounded-lg bg-gray-100 dark:bg-gray-700 shadow-inner">
                    <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-2">
                      Current Operational Expenditure (OpEx) Limit
                    </h3>
                    <p className="text-4xl font-extrabold text-blue-600 dark:text-blue-400">
                      {formatCurrency(currentSettings?.oPExLimit)}
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                      This is the maximum amount allowed for single Operational
                      Expenditure requests.
                    </p>
                  </div>

                  {/* Update Form */}
                  <Card className="shadow-none border dark:border-gray-700">
                    <h3 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">
                      Update Max OpEx Limit
                    </h3>

                    <div className="flex flex-col gap-4">
                      <div>
                        <div className="mb-2 block">
                          <Label
                            htmlFor="opex"
                            value="New Maximum OpEx Amount"
                          />
                        </div>
                        <TextInput
                          id="opex"
                          placeholder="Enter Max Amount for OpEx"
                          required
                          name="opex"
                          value={newOpex}
                          type="number"
                          step="0.01"
                          // icon={HiCurrencyNaira}
                          onChange={(e) => setNewOpex(e.target.value)}
                          disabled={!canWriteSettings}
                        />
                      </div>

                      <Button
                        disabled={isUpdateDisabled || !canWriteSettings}
                        color="blue"
                        onClick={() => setShowConfirmModal(true)}
                      >
                        {loading ? (
                          <Spinner size="sm" className="mr-2" />
                        ) : (
                          "Update Limit"
                        )}
                      </Button>
                    </div>
                  </Card>
                </div>
              </Card>
            )}
      </DashboardLayout>

      {/* Confirmation Modal */}
      <Modal
        show={showConfirmModal}
        size="md"
        onClose={() => setShowConfirmModal(false)}
        popup
      >
        <Modal.Header />
        <Modal.Body>
          <div className="text-center">
            <HiOutlineExclamationCircle className="mx-auto mb-4 h-14 w-14 text-yellow-400 dark:text-gray-200" />
            <h3 className="mb-5 text-lg font-normal text-gray-500 dark:text-gray-400">
              Are you sure you want to change the Maximum OpEx Limit to:
              <br />
              <strong className="text-xl text-red-600 dark:text-red-400">
                {formatCurrency(newOpex)}
              </strong>
              ?
            </h3>
            <div className="flex justify-center gap-4">
              <Button color="failure" onClick={changeMaxOpex}>
                Yes, I'm sure
              </Button>
              <Button color="gray" onClick={() => setShowConfirmModal(false)}>
                Cancel
              </Button>
            </div>
          </div>
        </Modal.Body>
      </Modal>

      {/* Logout Modal */}
      <Modal
        show={showLogoutModal}
        size="md"
        onClose={() => setShowLogoutModal(false)}
        popup
      >
        <Modal.Header />
        <Modal.Body>
          <div className="text-center">
            <HiOutlineExclamationCircle className="mx-auto mb-4 h-14 w-14 text-gray-400 dark:text-gray-200" />
            <h3 className="mb-5 text-lg font-normal text-gray-500 dark:text-gray-400">
              {notification.message}
            </h3>
            <div className="flex justify-center gap-4">
              <Button color="failure" onClick={logOut}>
                Log Out
              </Button>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
}

export default SiteSettings;
